/**
 * API utility for fetching data from the backend
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom API Error class with structured error support
 */
class APIError extends Error {
  constructor(message, code = null, details = null, status = null, data = null) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.details = details;
    this.status = status;
    this.data = data;
  }
}

/**
 * Generic fetch wrapper with error handling
 * Supports structured error responses with code, message, details
 */
// Helper function that bypasses any potential interceptors
async function safeReadResponse(response) {
  // Try multiple methods to read the response
  try {
    const text = await response.text();
    return text;
  } catch (e1) {
    // If text() fails, the body was already consumed by platform interceptors
    // Return a status-based error message
    const statusMessages = {
      400: 'Invalid request',
      401: 'Please sign in to continue',
      403: 'Access denied',
      404: 'Resource not found',
      409: 'Conflict detected',
      422: 'Validation error',
      429: 'Too many attempts. Please try again later.',
      500: 'Server error. Please try again.',
      502: 'Service temporarily unavailable',
      503: 'Service temporarily unavailable',
    };
    const message = statusMessages[response.status] || `HTTP ${response.status}`;
    return JSON.stringify({ detail: message });
  }
}

async function fetchAPI(endpoint, options = {}) {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    
    // Read response text once
    const responseText = await safeReadResponse(response);
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { detail: responseText || `HTTP ${response.status}` };
      }
      
      // Handle structured error response: {code, message, details}
      if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        const structured = errorData.detail;
        throw new APIError(
          structured.message || `HTTP ${response.status}`,
          structured.code || null,
          structured.details || null,
          response.status,
          structured
        );
      }
      
      // Handle simple string error
      throw new APIError(
        errorData.detail || `HTTP ${response.status}`,
        null,
        null,
        response.status,
        errorData
      );
    }
    
    // Parse successful response
    return JSON.parse(responseText);
  } catch (error) {
    // If it's already an APIError, just rethrow
    if (error instanceof APIError) {
      throw error;
    }
    // For network errors, JSON parse errors on success responses, etc.
    console.error(`API Error (${endpoint}):`, error);
    throw new APIError(
      error.message || 'Network error',
      null,
      null,
      null,
      { detail: error.message }
    );
  }
}

// =====================================================
// SMALL FORMAT API
// =====================================================

export const smallFormatAPI = {
  getCategories: () => fetchAPI('/api/products/small-format/categories'),
  
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.featured !== undefined) queryParams.append('featured', params.featured);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/products/small-format${query ? `?${query}` : ''}`);
  },
  
  getProduct: (productId) => fetchAPI(`/api/products/${productId}`),
};

// =====================================================
// LARGE FORMAT API
// =====================================================

export const largeFormatAPI = {
  getCategories: () => fetchAPI('/api/products/large-format/categories'),
  
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.featured !== undefined) queryParams.append('featured', params.featured);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/products/large-format${query ? `?${query}` : ''}`);
  },
  
  getProduct: (productId) => fetchAPI(`/api/products/${productId}`),
};

// =====================================================
// SWAG API
// =====================================================

export const swagAPI = {
  getCategories: () => fetchAPI('/api/products/swag/categories'),
  
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.featured !== undefined) queryParams.append('featured', params.featured);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/products/swag${query ? `?${query}` : ''}`);
  },
  
  getProduct: (productId) => fetchAPI(`/api/products/${productId}`),
};

// =====================================================
// AWARDS API
// =====================================================

export const awardsAPI = {
  getCategories: () => fetchAPI('/api/products/awards/categories'),
  getSubcategories: () => fetchAPI('/api/products/awards/subcategories'),
  
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.subcategory) queryParams.append('subcategory', params.subcategory);
    if (params.featured !== undefined) queryParams.append('featured', params.featured);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/products/awards${query ? `?${query}` : ''}`);
  },
  
  getProduct: (productId) => fetchAPI(`/api/products/${productId}`),
};

// =====================================================
// GLOBAL SEARCH API
// =====================================================

export const searchAPI = {
  search: (query, limit = 20) => fetchAPI(`/api/products/search?q=${encodeURIComponent(query)}&limit=${limit}`),
};

// =====================================================
// CONTACT API
// =====================================================

export const contactAPI = {
  submit: (data) => fetchAPI('/api/contact/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// =====================================================
// AUTH API
// =====================================================

export const authAPI = {
  login: (email, password) => fetchAPI('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  }),
  
  register: (name, email, password) => fetchAPI('/api/auth/register', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ name, email, password }),
  }),
  
  googleSession: (sessionId) => fetchAPI('/api/auth/google/session', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ session_id: sessionId }),
  }),
  
  me: () => fetch(`${API_BASE_URL}/api/auth/me`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Not authenticated');
    return res.json();
  }),
  
  getPermissions: () => fetch(`${API_BASE_URL}/api/auth/me/permissions`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Not authenticated');
    return res.json();
  }),
  
  logout: () => fetchAPI('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  }),
  
  changePassword: (currentPassword, newPassword) => fetchAPI('/api/auth/change-password', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  }),
  
  // MFA login completion (after MFA verification)
  completeMfaLogin: (userId, challengeId) => fetchAPI('/api/auth/login/mfa-complete', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ user_id: userId, challenge_id: challengeId }),
  }),
};

// =====================================================
// MFA API (Multi-Factor Authentication)
// =====================================================

export const mfaAPI = {
  // Get MFA status for current user
  getStatus: () => fetchAPI('/api/mfa/status', {
    credentials: 'include',
  }),
  
  // Start MFA setup (email or totp)
  startSetup: (method = 'email') => fetchAPI('/api/mfa/setup/start', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ method }),
  }),
  
  // Verify MFA setup and enable
  verifySetup: (method, code) => fetchAPI('/api/mfa/setup/verify', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ method, code }),
  }),
  
  // Create MFA challenge (during login)
  createChallenge: (userId, method = null) => fetchAPI('/api/mfa/challenge', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, method }),
  }),
  
  // Verify MFA challenge (during login)
  verifyChallenge: (challengeId, code) => fetchAPI('/api/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ challenge_id: challengeId, code }),
  }),
  
  // Disable MFA
  disable: (code) => fetchAPI('/api/mfa/disable', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ code }),
  }),
  
  // Get backup codes (masked)
  getBackupCodes: () => fetchAPI('/api/mfa/backup-codes', {
    credentials: 'include',
  }),
  
  // Regenerate backup codes
  regenerateBackupCodes: (code) => fetchAPI('/api/mfa/backup-codes/regenerate', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ code }),
  }),
};

// =====================================================
// USER PROFILE API
// =====================================================

export const profileAPI = {
  // Get current user's profile
  getProfile: () => fetchAPI('/api/users/me', {
    credentials: 'include',
  }),
  
  // Update current user's profile
  updateProfile: (data) => fetchAPI('/api/users/me', {
    method: 'PATCH',
    credentials: 'include',
    body: JSON.stringify(data),
  }),
  
  // Upload avatar
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_BASE_URL}/api/users/me/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(res => {
      if (!res.ok) throw new Error('Failed to upload avatar');
      return res.json();
    });
  },
  
  // Delete avatar
  deleteAvatar: () => fetchAPI('/api/users/me/avatar', {
    method: 'DELETE',
    credentials: 'include',
  }),
  
  // Change email (requires password)
  changeEmail: (newEmail, password) => fetchAPI('/api/auth/change-email', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ new_email: newEmail, password }),
  }),
  
  // Forgot password
  forgotPassword: (email) => fetchAPI('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  
  // Reset password with token
  resetPassword: (token, newPassword) => fetchAPI('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  }),
  
  // Validate reset token
  validateResetToken: (token) => fetchAPI(`/api/auth/reset-password/validate/${token}`),
  
  // Get shipping addresses
  getAddresses: () => fetchAPI('/api/auth/addresses', {
    credentials: 'include',
  }),
  
  // Create shipping address
  createAddress: (address) => fetchAPI('/api/auth/addresses', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(address),
  }),
  
  // Update shipping address
  updateAddress: (addressId, data) => fetchAPI(`/api/auth/addresses/${addressId}`, {
    method: 'PATCH',
    credentials: 'include',
    body: JSON.stringify(data),
  }),
  
  // Delete shipping address
  deleteAddress: (addressId) => fetchAPI(`/api/auth/addresses/${addressId}`, {
    method: 'DELETE',
    credentials: 'include',
  }),
  
  // Set default address
  setDefaultAddress: (addressId) => fetchAPI(`/api/auth/addresses/${addressId}/set-default`, {
    method: 'PATCH',
    credentials: 'include',
  }),
  
  // Login activity
  getLoginActivity: (limit = 10) => fetchAPI(`/api/auth/activity?limit=${limit}`, {
    credentials: 'include',
  }),
  
  // Active sessions
  getSessions: () => fetchAPI('/api/auth/sessions', {
    credentials: 'include',
  }),
  
  // Revoke specific session
  revokeSession: (sessionId) => fetchAPI(`/api/auth/sessions/${sessionId}`, {
    method: 'DELETE',
    credentials: 'include',
  }),
  
  // Revoke all other sessions
  revokeAllSessions: () => fetchAPI('/api/auth/sessions', {
    method: 'DELETE',
    credentials: 'include',
  }),
  
  // Send verification email
  sendVerificationEmail: () => fetchAPI('/api/auth/verify-email/send', {
    method: 'POST',
    credentials: 'include',
  }),
  
  // Confirm email verification
  confirmEmailVerification: (token) => fetchAPI(`/api/auth/verify-email/confirm?token=${token}`, {
    method: 'POST',
  }),
};

// =====================================================
// ORDERS API
// =====================================================

export const ordersAPI = {
  create: (orderData) => fetchAPI('/api/orders/', {
    method: 'POST',
    body: JSON.stringify(orderData),
  }),
  
  get: (orderId) => fetchAPI(`/api/orders/${orderId}`),
  
  track: (orderId, email) => fetchAPI(`/api/orders/track/${orderId}?email=${encodeURIComponent(email)}`),
  
  getUserOrders: (userId) => fetchAPI(`/api/orders/user/${userId}`, {
    credentials: 'include',
  }),
};

// =====================================================
// ADMIN API
// =====================================================

export const adminAPI = {
  getDashboard: () => fetch(`${API_BASE_URL}/api/admin/dashboard`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  // Get distinct filter options for orders
  getOrderFilterOptions: () => fetch(`${API_BASE_URL}/api/admin/orders/filter-options`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Failed to fetch filter options');
    return res.json();
  }),
  
  // Get order metrics for dashboard cards (Open, In-Progress, Overdue)
  getOrderMetrics: () => fetch(`${API_BASE_URL}/api/admin/orders/metrics`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Failed to fetch order metrics');
    return res.json();
  }),
  
  // Orders - Enhanced with server-side sorting, filtering, pagination
  getOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.status) queryParams.append('status', params.status);
    if (params.payment_status) queryParams.append('payment_status', params.payment_status);
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.contact_id) queryParams.append('contact_id', params.contact_id);
    // Date range
    if (params.created_from) queryParams.append('created_from', params.created_from);
    if (params.created_to) queryParams.append('created_to', params.created_to);
    // Amount range
    if (params.total_min) queryParams.append('total_min', params.total_min);
    if (params.total_max) queryParams.append('total_max', params.total_max);
    // Unified Quote/Invoice filters (new)
    if (params.has_quote) queryParams.append('has_quote', params.has_quote);
    if (params.has_invoice) queryParams.append('has_invoice', params.has_invoice);
    if (params.quote_status) queryParams.append('quote_status', params.quote_status);
    if (params.invoice_status) queryParams.append('invoice_status', params.invoice_status);
    // Legacy params (backwards compatibility)
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetch(`${API_BASE_URL}/api/admin/orders${query ? `?${query}` : ''}`, {
      credentials: 'include',
    }).then(res => {
      if (!res.ok) throw new Error('Admin access required');
      return res.json();
    });
  },
  
  getOrder: (orderId) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  updateOrder: (orderId, data) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  // Cancel order
  cancelOrder: (orderId, data) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(async res => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to cancel order');
    }
    return res.json();
  }),
  
  // Combine orders preview - fetch and merge line items from multiple orders
  getCombineOrdersPreview: (orderIds) => fetch(`${API_BASE_URL}/api/admin/orders/combine-preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_ids: orderIds }),
  }).then(async res => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to load orders for combining');
    }
    return res.json();
  }),
  
  // Create admin order (P1)
  createOrder: (data) => fetch(`${API_BASE_URL}/api/admin/orders`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(async res => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create order');
    }
    return res.json();
  }),
  
  bulkDeleteOrders: (data) => fetch(`${API_BASE_URL}/api/admin/orders/bulk-delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(async res => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete orders');
    }
    return res.json();
  }),
  
  restoreOrder: (orderId) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/restore`, {
    method: 'POST',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Failed to restore order');
    return res.json();
  }),
  
  // Contacts
  getContacts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetch(`${API_BASE_URL}/api/admin/contacts${query ? `?${query}` : ''}`, {
      credentials: 'include',
    }).then(res => {
      if (!res.ok) throw new Error('Admin access required');
      return res.json();
    });
  },
  
  getContact: (contactId) => fetch(`${API_BASE_URL}/api/admin/contacts/${contactId}`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  updateContactStatus: (contactId, status) => fetch(`${API_BASE_URL}/api/admin/contacts/${contactId}/status?status=${status}`, {
    method: 'PATCH',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  // Products
  createProduct: (data) => fetch(`${API_BASE_URL}/api/admin/products`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  updateProduct: (section, productId, data) => fetch(`${API_BASE_URL}/api/admin/products/${section}/${productId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  deleteProduct: (section, productId) => fetch(`${API_BASE_URL}/api/admin/products/${section}/${productId}`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  // Users
  getUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.role) queryParams.append('role', params.role);
    if (params.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return fetch(`${API_BASE_URL}/api/admin/users${query ? `?${query}` : ''}`, {
      credentials: 'include',
    }).then(res => {
      if (!res.ok) throw new Error('Admin access required');
      return res.json();
    });
  },
  
  // Update user settings (allow_pay_later, etc.)
  updateUser: (userId, data) => fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Failed to update user');
    return res.json();
  }),
  
  updateUserRole: (userId, role) => fetch(`${API_BASE_URL}/api/admin/users/${userId}/role?role=${role}`, {
    method: 'PATCH',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),

  // Update user with multiple roles (with audit logging)
  updateUserRoles: (userId, roles, reason = null, source = 'admin_ui') => fetch(`${API_BASE_URL}/api/admin/users/${userId}/roles-with-audit`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roles, reason, source }),
  }).then(res => {
    if (!res.ok) throw new Error('Failed to update roles');
    return res.json();
  }),

  // Role Audit Logs
  getRoleAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.target_user_id) queryParams.append('target_user_id', params.target_user_id);
    if (params.changed_by_user_id) queryParams.append('changed_by_user_id', params.changed_by_user_id);
    if (params.days) queryParams.append('days', params.days);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetch(`${API_BASE_URL}/api/admin/role-audit-logs${query ? `?${query}` : ''}`, {
      credentials: 'include',
    }).then(res => {
      if (!res.ok) throw new Error('Admin access required');
      return res.json();
    });
  },

  getRoleAuditStats: () => fetch(`${API_BASE_URL}/api/admin/role-audit-logs/stats`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Admin access required');
    return res.json();
  }),
  
  // Artwork Approval
  approveArtwork: (orderId) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/approve-artwork`, {
    method: 'POST',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) throw new Error('Failed to approve artwork');
    return res.json();
  }),
  
  // Update Order Status
  updateOrderStatus: (orderId, data) => fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => {
    if (!res.ok) throw new Error('Failed to update order');
    return res.json();
  }),
};

// =====================================================
// PRODUCT TYPES API (Data-driven product type management)
// =====================================================

export const productTypesAPI = {
  // Public - get active product types for navigation/filtering
  getProductTypes: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.include_stats) queryParams.append('include_stats', params.include_stats);
    const query = queryParams.toString();
    return fetchAPI(`/api/product-types${query ? `?${query}` : ''}`);
  },
  
  getProductType: (slug) => fetchAPI(`/api/product-types/${slug}`),
  
  getValidSlugs: () => fetchAPI('/api/product-types/slugs/valid'),
  
  // Admin endpoints
  adminGetProductTypes: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const query = queryParams.toString();
    return fetchAPI(`/api/product-types/admin/list${query ? `?${query}` : ''}`);
  },
  
  adminCreateProductType: (data) => fetchAPI('/api/product-types/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  adminUpdateProductType: (productTypeId, data) => fetchAPI(`/api/product-types/admin/${productTypeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  adminReorderProductTypes: (orders) => fetchAPI('/api/product-types/admin/reorder', {
    method: 'PUT',
    body: JSON.stringify({ orders }),
  }),
  
  adminToggleProductTypeActive: (productTypeId) => fetchAPI(`/api/product-types/admin/${productTypeId}/toggle-active`, {
    method: 'PUT',
  }),
  
  adminDeleteProductType: (productTypeId) => fetchAPI(`/api/product-types/admin/${productTypeId}`, {
    method: 'DELETE',
  }),
};

// =====================================================
// UNIFIED API (New consolidated products & categories)
// =====================================================

export const unifiedAPI = {
  // Products
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.manufacturer_id) queryParams.append('manufacturer_id', params.manufacturer_id);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return fetchAPI(`/api/products${query ? `?${query}` : ''}`);
  },
  
  getProduct: (productId) => fetchAPI(`/api/products/${productId}`),
  
  getProductBySlug: (slug) => fetchAPI(`/api/products/by-slug/${slug}`),
  
  getProductStats: () => fetchAPI('/api/products/stats/counts'),
  
  // Admin products (includes cost_price, manufacturer_sku) - Enhanced
  adminGetProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.manufacturer_id) queryParams.append('manufacturer_id', params.manufacturer_id);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    // Price range
    if (params.price_min) queryParams.append('price_min', params.price_min);
    if (params.price_max) queryParams.append('price_max', params.price_max);
    // Legacy params
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return fetchAPI(`/api/products/admin/list${query ? `?${query}` : ''}`);
  },
  
  adminGetProduct: (productId) => fetchAPI(`/api/products/admin/${productId}`),
  
  adminCreateProduct: (data) => fetchAPI('/api/products/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  adminUpdateProduct: (productId, data) => fetchAPI(`/api/products/admin/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  adminDeleteProduct: (productId) => fetchAPI(`/api/products/admin/${productId}`, {
    method: 'DELETE',
  }),
  
  // Bulk actions
  adminBulkDeleteProducts: (data) => fetchAPI('/api/products/admin/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  adminRestoreProduct: (productId) => fetchAPI(`/api/products/admin/${productId}/restore`, {
    method: 'POST',
  }),
  
  // Categories
  getCategories: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.parent_id !== undefined) queryParams.append('parent_id', params.parent_id || '');
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.include_counts !== undefined) queryParams.append('include_counts', params.include_counts);
    const query = queryParams.toString();
    return fetchAPI(`/api/categories${query ? `?${query}` : ''}`);
  },
  
  getCategoryTree: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const query = queryParams.toString();
    return fetchAPI(`/api/categories/tree${query ? `?${query}` : ''}`);
  },
  
  getCategoriesByType: (productType, includeTree = false) => {
    return fetchAPI(`/api/categories/by-type/${productType}?include_tree=${includeTree}`);
  },
  
  getCategory: (categoryId) => fetchAPI(`/api/categories/${categoryId}`),
  
  getCategoryDescendants: (categoryId) => fetchAPI(`/api/categories/${categoryId}/descendants`),
  
  // Admin categories
  adminGetCategories: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const query = queryParams.toString();
    return fetchAPI(`/api/categories/admin/list${query ? `?${query}` : ''}`);
  },
  
  adminGetCategoryTree: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    const query = queryParams.toString();
    return fetchAPI(`/api/categories/admin/tree${query ? `?${query}` : ''}`);
  },
  
  adminCreateCategory: (data) => fetchAPI('/api/categories/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  adminUpdateCategory: (categoryId, data) => fetchAPI(`/api/categories/admin/${categoryId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  adminReorderCategories: (orders) => fetchAPI('/api/categories/admin/reorder', {
    method: 'PUT',
    body: JSON.stringify({ orders }),
  }),
  
  adminDeleteCategory: (categoryId, reassignTo = null) => {
    const params = reassignTo ? `?reassign_to=${reassignTo}` : '';
    return fetchAPI(`/api/categories/admin/${categoryId}${params}`, {
      method: 'DELETE',
    });
  },
  
  adminToggleCategoryActive: (categoryId) => fetchAPI(`/api/categories/admin/${categoryId}/toggle-active`, {
    method: 'PUT',
  }),
  
  adminCopyCategory: (data) => fetchAPI('/api/categories/admin/copy', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// =====================================================
// CATALOG API (DEPRECATED - Use unifiedAPI instead)
// This API is deprecated and will be removed in a future release.
// All consumers should migrate to unifiedAPI.
// =====================================================

const logDeprecationWarning = (method) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[DEPRECATED] catalogAPI.${method}() is deprecated. Use unifiedAPI instead.`);
  }
};

export const catalogAPI = {
  // Get all categories across sections - DEPRECATED
  getCategories: (params = {}) => {
    logDeprecationWarning('getCategories');
    // Map to unifiedAPI format
    const unifiedParams = {
      product_type: params.section,
      include_counts: true
    };
    return unifiedAPI.getCategories(unifiedParams);
  },
  
  // Get products with advanced sorting and filtering - DEPRECATED
  getProducts: (section, params = {}) => {
    logDeprecationWarning('getProducts');
    // Map to unifiedAPI format
    const unifiedParams = {
      product_type: section?.replace('-', '_'),
      category_id: params.categories,
      page: params.page,
      limit: params.limit,
      sort: params.sort,
      search: params.search
    };
    return unifiedAPI.getProducts(unifiedParams);
  },
  
  // Get sort options - DEPRECATED
  getSortOptions: () => {
    logDeprecationWarning('getSortOptions');
    // Return static sort options since unifiedAPI doesn't have this endpoint
    return Promise.resolve({
      sort_options: [
        { value: 'name_asc', label: 'Name (A-Z)' },
        { value: 'name_desc', label: 'Name (Z-A)' },
        { value: 'price_asc', label: 'Price (Low to High)' },
        { value: 'price_desc', label: 'Price (High to Low)' },
        { value: 'created_desc', label: 'Newest First' }
      ]
    });
  },
  
  // Search across all sections - DEPRECATED
  search: (query, params = {}) => {
    logDeprecationWarning('search');
    // Map to unifiedAPI format
    const unifiedParams = {
      product_type: params.section?.replace('-', '_'),
      search: query,
      sort: params.sort,
      limit: params.limit
    };
    return unifiedAPI.getProducts(unifiedParams);
  },
};

// =====================================================
// CRM API
// =====================================================

export const crmAPI = {
  // Accounts - Enhanced with server-side sorting, filtering, pagination
  getAccounts: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.owner_user_id) queryParams.append('owner_user_id', params.owner_user_id);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    // Legacy params
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/crm/accounts${query ? `?${query}` : ''}`);
  },
  
  getAccount: (accountId) => fetchAPI(`/api/crm/accounts/${accountId}`),
  
  createAccount: (data) => fetchAPI('/api/crm/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateAccount: (accountId, data) => fetchAPI(`/api/crm/accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Bulk actions for accounts
  bulkDeleteAccounts: (data) => fetchAPI('/api/crm/accounts/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  restoreAccount: (accountId) => fetchAPI(`/api/crm/accounts/${accountId}/restore`, {
    method: 'POST',
  }),
  
  // Contacts - Enhanced with server-side sorting, filtering, pagination
  getContacts: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.unassigned) queryParams.append('unassigned', 'true');
    // Legacy params
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/crm/contacts${query ? `?${query}` : ''}`);
  },
  
  getContact: (contactId) => fetchAPI(`/api/crm/contacts/${contactId}`),
  
  getContactWithRelated: (contactId) => fetchAPI(`/api/crm/contacts/${contactId}/related`),
  
  checkEmailExists: (email) => fetchAPI(`/api/crm/contacts/check-email?email=${encodeURIComponent(email)}`),
  
  createContact: (data) => fetchAPI('/api/crm/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateContact: (contactId, data) => fetchAPI(`/api/crm/contacts/${contactId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Bulk actions for contacts
  bulkDeleteContacts: (data) => fetchAPI('/api/crm/contacts/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  restoreContact: (contactId) => fetchAPI(`/api/crm/contacts/${contactId}/restore`, {
    method: 'POST',
  }),
  
  // Ship-To Contacts - Get contacts for a customer sorted for ship-to selection
  getCustomerContactsForShipTo: (customerId) => fetchAPI(`/api/crm/customers/${customerId}/contacts?purpose=ship_to`),
  
  // Set contact as default ship-to
  setDefaultShipTo: (contactId) => fetchAPI(`/api/crm/contacts/${contactId}`, {
    method: 'PUT',
    body: JSON.stringify({ is_default_ship_to: true }),
  }),
  
  // Leads
  getLeads: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.owner_user_id) queryParams.append('owner_user_id', params.owner_user_id);
    if (params.stage) queryParams.append('stage', params.stage);
    if (params.source) queryParams.append('source', params.source);
    if (params.overdue) queryParams.append('overdue', 'true');
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/crm/leads${query ? `?${query}` : ''}`);
  },
  
  getLead: (leadId) => fetchAPI(`/api/crm/leads/${leadId}`),
  
  createLead: (data) => fetchAPI('/api/crm/leads', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateLead: (leadId, data) => fetchAPI(`/api/crm/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  getLeadsNeedingAction: () => fetchAPI('/api/crm/leads/needs-next-step'),
  
  // Activities
  getActivities: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.lead_id) queryParams.append('lead_id', params.lead_id);
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.contact_id) queryParams.append('contact_id', params.contact_id);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return fetchAPI(`/api/crm/activities${query ? `?${query}` : ''}`);
  },
  
  createActivity: (data) => fetchAPI('/api/crm/activities', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Tasks
  getTasks: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.my_tasks) queryParams.append('my_tasks', 'true');
    if (params.due_today) queryParams.append('due_today', 'true');
    if (params.overdue) queryParams.append('overdue', 'true');
    if (params.status) queryParams.append('status', params.status);
    if (params.owner_user_id) queryParams.append('owner_user_id', params.owner_user_id);
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.lead_id) queryParams.append('lead_id', params.lead_id);
    if (params.quote_id) queryParams.append('quote_id', params.quote_id);
    if (params.order_id) queryParams.append('order_id', params.order_id);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/crm/tasks${query ? `?${query}` : ''}`);
  },
  
  createTask: (data) => fetchAPI('/api/crm/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateTask: (taskId, data) => fetchAPI(`/api/crm/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  completeTask: (taskId) => fetchAPI(`/api/crm/tasks/${taskId}/complete`, {
    method: 'POST',
  }),
  
  // Account Detail - Full customer view with related data
  getAccountDetail: (accountId) => fetchAPI(`/api/crm/accounts/${accountId}/detail`),
  
  // Customer Documents
  deleteCustomerDocument: (documentId) => fetchAPI(`/api/crm/documents/${documentId}`, {
    method: 'DELETE',
  }),
};

// =====================================================
// QUOTES API
// =====================================================

// =====================================================
// ORDER LIFECYCLE API (Unified Quote/Invoice on Orders)
// =====================================================
// This replaces the separate quotesAPI and invoicesAPI
// All quote and invoice operations now happen on orders

export const orderLifecycleAPI = {
  // ==================
  // STATUS OPERATIONS (P1 - Three independent tracks)
  // ==================
  
  // Update ops status (always editable): OPEN | IN_PROGRESS | COMPLETED
  updateOpsStatus: (orderId, ops_status) => fetchAPI(`/api/orders/${orderId}/ops-status`, {
    method: 'PATCH',
    body: JSON.stringify({ ops_status }),
  }),
  
  // Update fulfillment status (always editable): NONE | READY | SHIPPED
  updateFulfillmentStatus: (orderId, fulfillment_status, tracking_number = null) => fetchAPI(`/api/orders/${orderId}/fulfillment-status`, {
    method: 'PATCH',
    body: JSON.stringify({ fulfillment_status, tracking_number }),
  }),
  
  // Update customer info (blocked after invoice issued)
  updateCustomer: (orderId, data) => fetchAPI(`/api/orders/${orderId}/customer`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Update ship-to information (centralized endpoint)
  // Either provide contactId or manualEntry (for one-off addresses)
  // Returns updated order so UI doesn't need another fetch
  updateShipTo: (orderId, { contactId, manualEntry, adminOverride = false }) => fetchAPI(`/api/orders/${orderId}/ship-to`, {
    method: 'PATCH',
    body: JSON.stringify({ 
      contact_id: contactId, 
      manual_entry: manualEntry,
      admin_override: adminOverride 
    }),
  }),
  
  // Update items/pricing (blocked after invoice issued)
  updateItems: (orderId, data) => fetchAPI(`/api/orders/${orderId}/items`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Update adjustment fields (for post-PAID changes, always editable)
  updateAdjustment: (orderId, data) => fetchAPI(`/api/orders/${orderId}/adjustment`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Update internal notes (always editable)
  updateNotes: (orderId, internal_notes) => fetchAPI(`/api/orders/${orderId}/notes`, {
    method: 'PATCH',
    body: JSON.stringify({ internal_notes }),
  }),
  
  // Update order summary (displays on invoice PDF, blocked after invoice issued)
  updateSummary: (orderId, order_summary) => fetchAPI(`/api/orders/${orderId}/summary`, {
    method: 'PATCH',
    body: JSON.stringify({ order_summary }),
  }),
  
  // Update due date (always editable, never locked by invoice)
  updateDueDate: (orderId, due_at) => fetchAPI(`/api/orders/${orderId}/due-date`, {
    method: 'PATCH',
    body: JSON.stringify({ due_at }),
  }),
  
  // Update discount (Wave-style structured discount)
  // type: "PERCENT" (0-100) or "AMOUNT" (flat dollar amount)
  // value: The discount value (0 to remove discount)
  // description: Optional description for the discount
  // Blocked after invoice issued, allowed when NONE or VOID
  updateDiscount: (orderId, type, value, description = null) => fetchAPI(`/api/orders/${orderId}/discount`, {
    method: 'PATCH',
    body: JSON.stringify({ type, value, description }),
  }),
  
  // ==================
  // QUOTE OPERATIONS (optional, never blocks invoice)
  // ==================
  
  // Issue a quote for an order
  issueQuote: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/quote/issue`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Send quote to customer
  sendQuote: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/quote/send`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update quote status (approve, reject, expire)
  updateQuoteStatus: (orderId, status, note = null) => fetchAPI(`/api/orders/${orderId}/quote/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, note }),
  }),
  
  // ==================
  // INVOICE OPERATIONS
  // ==================
  
  // Issue an invoice for an order
  issueInvoice: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/invoice/issue`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Send invoice to customer
  sendInvoice: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/invoice/send`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update invoice status (PAID, VOID)
  updateInvoiceStatus: (orderId, status, reason = null, note = null) => fetchAPI(`/api/orders/${orderId}/invoice/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason, note }),
  }),
  
  // Void invoice (admin only)
  voidInvoice: (orderId, reason) => fetchAPI(`/api/orders/${orderId}/invoice/void?reason=${encodeURIComponent(reason)}`, {
    method: 'POST',
  }),
  
  // Re-issue invoice after VOID (creates new invoice number)
  reissueInvoice: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/invoice/reissue`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update invoice reminder schedule (P4 - manual checkboxes)
  updateInvoiceReminders: (orderId, beforeDue = [], afterDue = []) => fetchAPI(`/api/orders/${orderId}/invoice/reminders`, {
    method: 'PUT',
    body: JSON.stringify({ before_due: beforeDue, after_due: afterDue }),
  }),
  
  // ==================
  // ORDER NOTIFICATIONS
  // ==================
  
  // Send Order Shipped notification
  sendShippedNotification: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/notification/shipped`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Send Ready for Pickup notification
  sendReadyForPickupNotification: (orderId, data = {}) => fetchAPI(`/api/orders/${orderId}/notification/ready-for-pickup`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // ==================
  // SALES TAX OPERATIONS
  // ==================
  
  // Update order's sales tax settings
  updateSalesTax: (orderId, data) => fetchAPI(`/api/orders/${orderId}/sales-tax`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Download invoice PDF (renders from snapshot)
  downloadInvoicePdf: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/invoice/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.blob();
  },
  
  // Get invoice PDF URL (for download)
  getInvoicePdfUrl: (orderId, options = {}) => {
    const params = new URLSearchParams();
    if (options.coverPage) params.append('cover_page', 'true');
    if (options.preview) params.append('preview', 'true');
    const queryString = params.toString();
    return `${API_BASE_URL}/api/orders/${orderId}/invoice/pdf${queryString ? '?' + queryString : ''}`;
  },
  
  // Get invoice PDF URL for preview (opens inline in browser)
  getInvoicePreviewUrl: (orderId, options = {}) => {
    return `${API_BASE_URL}/api/orders/${orderId}/invoice/pdf?preview=true${options.coverPage ? '&cover_page=true' : ''}`;
  },
  
  // Download quote PDF (renders from snapshot)
  downloadQuotePdf: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/quote/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.blob();
  },
  
  // Get quote PDF URL (for download)
  getQuotePdfUrl: (orderId, options = {}) => {
    const params = new URLSearchParams();
    if (options.coverPage) params.append('cover_page', 'true');
    if (options.preview) params.append('preview', 'true');
    const queryString = params.toString();
    return `${API_BASE_URL}/api/orders/${orderId}/quote/pdf${queryString ? '?' + queryString : ''}`;
  },
  
  // Get quote PDF URL for preview (opens inline in browser)
  getQuotePreviewUrl: (orderId, options = {}) => {
    return `${API_BASE_URL}/api/orders/${orderId}/quote/pdf?preview=true${options.coverPage ? '&cover_page=true' : ''}`;
  },
};

// =====================================================
// DEPRECATED: QUOTES API (Routes removed - will fail)
// Use orderLifecycleAPI instead
// =====================================================
export const quotesAPI = {
  // DEPRECATED - returns error message
  getQuotes: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI and filter orders by quote_status.')),
  getQuote: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI.')),
  createQuote: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI.issueQuote(orderId).')),
  updateQuote: () => Promise.reject(new Error('quotesAPI is deprecated.')),
  sendQuote: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI.sendQuote(orderId).')),
  approveQuote: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI.updateQuoteStatus(orderId, "approved").')),
  rejectQuote: () => Promise.reject(new Error('quotesAPI is deprecated. Use orderLifecycleAPI.updateQuoteStatus(orderId, "rejected").')),
  convertToOrder: () => Promise.reject(new Error('quotesAPI is deprecated. Orders are now unified with quotes.')),
};

// =====================================================
// OPERATIONS API (Work Orders, Proofs)
// =====================================================

export const operationsAPI = {
  // Extended Orders
  getOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.payment_status) queryParams.append('payment_status', params.payment_status);
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/orders/operations${query ? `?${query}` : ''}`);
  },
  
  updateOrderStatus: (orderId, data) => fetchAPI(`/api/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Order-Level Artwork (P1 Contract: Simplified)
  getOrderArtwork: (orderId) => fetchAPI(`/api/orders/${orderId}/artwork`),
  
  uploadOrderArtwork: async (orderId, files, notes = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (notes) formData.append('notes', notes);
    
    const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/artwork/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },
  
  updateArtworkStatus: (orderId, status, notes = null) => {
    const params = new URLSearchParams({ status });
    if (notes) params.append('notes', notes);
    return fetchAPI(`/api/orders/${orderId}/artwork/status?${params.toString()}`, {
      method: 'PATCH',
    });
  },
  
  cleanupArtworkProofs: () => fetchAPI('/api/artwork/admin/cleanup', {
    method: 'POST',
  }),
  
  // DEPRECATED: Artwork Proofs (kept for backward compatibility, will return 410)
  getProofs: (orderId) => fetchAPI(`/api/orders/${orderId}/proofs`),
  
  createProof: (orderId) => fetchAPI(`/api/orders/${orderId}/proofs`, {
    method: 'POST',
  }),
  
  sendProof: (orderId, proofId) => fetchAPI(`/api/orders/${orderId}/proofs/${proofId}/send`, {
    method: 'POST',
  }),
  
  approveProof: (orderId, proofId, data) => fetchAPI(`/api/orders/${orderId}/proofs/${proofId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Work Orders (P1 Contract: One WO per Order)
  getWorkOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.department) queryParams.append('department', params.department);
    if (params.status) queryParams.append('status', params.status);
    if (params.order_id) queryParams.append('order_id', params.order_id);
    if (params.assigned_to_user_id) queryParams.append('assigned_to_user_id', params.assigned_to_user_id);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/work-orders${query ? `?${query}` : ''}`);
  },
  
  getWorkOrder: (workOrderId) => fetchAPI(`/api/work-orders/${workOrderId}`),
  
  updateWorkOrder: (workOrderId, data) => fetchAPI(`/api/work-orders/${workOrderId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Update work order status with validation (P1 Contract)
  updateWorkOrderStatus: (workOrderId, status) => fetchAPI(`/api/work-orders/${workOrderId}/status?status=${status}`, {
    method: 'PATCH',
  }),
  
  // Get single work order for an order (P1 Contract: one WO per order)
  getOrderWorkOrder: (orderId) => fetchAPI(`/api/orders/${orderId}/work-order`),
  
  // Generate single work order for an order (IDEMPOTENT - P1 Contract)
  generateWorkOrder: (orderId) => fetchAPI(`/api/work-orders/generate?order_id=${orderId}`, {
    method: 'POST',
  }),
  
  // Admin cleanup for migration
  cleanupDuplicateWorkOrders: () => fetchAPI('/api/work-orders/admin/cleanup', {
    method: 'POST',
  }),
  
  // Order operations view
  getOrderOperations: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return fetchAPI(`/api/orders${query ? `?${query}` : ''}`);
  },
};

// Alias for backwards compatibility
export const opsAPI = operationsAPI;

// =====================================================
// INVOICES API
// =====================================================

// =====================================================
// DEPRECATED: INVOICES API (Routes removed - will fail)
// Use orderLifecycleAPI instead for order-based invoice operations
// Use arInvoicesAPI for AR cockpit / invoice list views
// =====================================================
export const invoicesAPI = {
  // DEPRECATED - returns error message
  getInvoices: () => Promise.reject(new Error('invoicesAPI is deprecated. Use arInvoicesAPI.list() for AR cockpit.')),
  getInvoice: () => Promise.reject(new Error('invoicesAPI is deprecated. Use order data with invoice_snapshot.')),
  getStats: () => Promise.reject(new Error('invoicesAPI is deprecated. Use arInvoicesAPI.getStats().')),
  createInvoice: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.issueInvoice(orderId).')),
  createFromOrder: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.issueInvoice(orderId).')),
  updateInvoice: () => Promise.reject(new Error('invoicesAPI is deprecated. Invoice snapshots are immutable.')),
  updateStatus: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.updateInvoiceStatus(orderId, status).')),
  bulkDelete: () => Promise.reject(new Error('invoicesAPI is deprecated.')),
  restore: () => Promise.reject(new Error('invoicesAPI is deprecated.')),
  downloadPdf: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.downloadInvoicePdf(orderId).')),
  getPdfUrl: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.getInvoicePdfUrl(orderId).')),
  getPdfPreviewUrl: () => Promise.reject(new Error('invoicesAPI is deprecated.')),
  getEmailTemplates: () => fetchAPI('/api/invoices/email-templates'), // Keep this if templates are reused
  sendEmail: () => Promise.reject(new Error('invoicesAPI is deprecated. Use orderLifecycleAPI.sendInvoice(orderId).')),
};

// =====================================================
// AR INVOICES API (P1 - Accounts Receivable Cockpit)
// New invoice collection endpoints for AR visibility
// =====================================================
export const arInvoicesAPI = {
  /**
   * List invoices with filters, pagination, and sorting
   * @param {Object} params - Query parameters
   * @param {string[]} [params.status] - Status filter (multi-select): DRAFT, ISSUED, SENT, PAID, VOID, OVERDUE
   * @param {string} [params.account_id] - Filter by customer account ID
   * @param {string} [params.due_from] - Due date from (YYYY-MM-DD)
   * @param {string} [params.due_to] - Due date to (YYYY-MM-DD)
   * @param {string} [params.issued_from] - Issued date from (YYYY-MM-DD)
   * @param {string} [params.issued_to] - Issued date to (YYYY-MM-DD)
   * @param {string} [params.search] - Global search (invoice_number, order_id, account_name)
   * @param {string} [params.sort_by] - Sort field: due_at, issued_at, total, balance_due_cents, invoice_number, order_id
   * @param {string} [params.sort_dir] - Sort direction: asc, desc
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.page_size] - Page size (default: 25, max: 100)
   */
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Handle status array (multi-select)
    if (params.status && Array.isArray(params.status)) {
      params.status.forEach(s => queryParams.append('status', s));
    } else if (params.status) {
      queryParams.append('status', params.status);
    }
    
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.due_from) queryParams.append('due_from', params.due_from);
    if (params.due_to) queryParams.append('due_to', params.due_to);
    if (params.issued_from) queryParams.append('issued_from', params.issued_from);
    if (params.issued_to) queryParams.append('issued_to', params.issued_to);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/invoices${query ? `?${query}` : ''}`);
  },
  
  /**
   * Get AR statistics (Total AR, Overdue AR, Overdue Count)
   * Returns both global stats and filtered stats when filters are applied
   * @param {Object} params - Same filters as list endpoint
   */
  getStats: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.status && Array.isArray(params.status)) {
      params.status.forEach(s => queryParams.append('status', s));
    } else if (params.status) {
      queryParams.append('status', params.status);
    }
    
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.due_from) queryParams.append('due_from', params.due_from);
    if (params.due_to) queryParams.append('due_to', params.due_to);
    if (params.issued_from) queryParams.append('issued_from', params.issued_from);
    if (params.issued_to) queryParams.append('issued_to', params.issued_to);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/invoices/stats${query ? `?${query}` : ''}`);
  },
  
  /**
   * Export invoices to CSV
   * @param {Object} params - Same filters as list endpoint
   * @returns {Promise<Blob>} CSV file blob
   */
  exportCsv: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.status && Array.isArray(params.status)) {
      params.status.forEach(s => queryParams.append('status', s));
    } else if (params.status) {
      queryParams.append('status', params.status);
    }
    
    if (params.account_id) queryParams.append('account_id', params.account_id);
    if (params.due_from) queryParams.append('due_from', params.due_from);
    if (params.due_to) queryParams.append('due_to', params.due_to);
    if (params.issued_from) queryParams.append('issued_from', params.issued_from);
    if (params.issued_to) queryParams.append('issued_to', params.issued_to);
    
    const query = queryParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/invoices/export/csv${query ? `?${query}` : ''}`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to export CSV');
    }
    
    return response.blob();
  },
  
  /**
   * Send payment reminder for an invoice
   * @param {string} invoiceId - Invoice ID
   */
  sendReminder: (invoiceId) => {
    return fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/send-reminder`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(async res => {
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Failed to send reminder' }));
        throw new Error(error.detail || 'Failed to send reminder');
      }
      return res.json();
    });
  },
};

// =====================================================
// VENDORS API (P0 Expenses Module)
// =====================================================

export const vendorsAPI = {
  getVendors: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.q) queryParams.append('q', params.q);
    if (params.vendor_type) queryParams.append('vendor_type', params.vendor_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/vendors${query ? `?${query}` : ''}`);
  },
  
  getVendor: (vendorId) => fetchAPI(`/api/vendors/${vendorId}`),
  
  getVendorStats: (vendorId) => fetchAPI(`/api/vendors/${vendorId}/stats`),
  
  createVendor: (data) => fetchAPI('/api/vendors', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateVendor: (vendorId, data) => fetchAPI(`/api/vendors/${vendorId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deactivateVendor: (vendorId) => fetchAPI(`/api/vendors/${vendorId}/deactivate`, {
    method: 'POST',
  }),
  
  reactivateVendor: (vendorId) => fetchAPI(`/api/vendors/${vendorId}/reactivate`, {
    method: 'POST',
  }),
  
  getVendorTypes: () => fetchAPI('/api/vendors/types/list'),
  
  // Vendor Contacts
  getContactRoles: () => fetchAPI('/api/vendors/contact-roles/list'),
  
  addContact: (vendorId, data) => fetchAPI(`/api/vendors/${vendorId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateContact: (vendorId, contactId, data) => fetchAPI(`/api/vendors/${vendorId}/contacts/${contactId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteContact: (vendorId, contactId) => fetchAPI(`/api/vendors/${vendorId}/contacts/${contactId}`, {
    method: 'DELETE',
  }),
  
  setPrimaryContact: (vendorId, contactId) => fetchAPI(`/api/vendors/${vendorId}/contacts/${contactId}/set-primary`, {
    method: 'POST',
  }),
};

// =====================================================
// PURCHASE ORDERS API (P0 Expenses Module)
// =====================================================

export const purchaseOrdersAPI = {
  // Enhanced list with server-side sorting, filtering, pagination
  getPurchaseOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.status) queryParams.append('status', params.status);
    if (params.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    // Date range
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    // Amount range
    if (params.total_min) queryParams.append('total_min', params.total_min);
    if (params.total_max) queryParams.append('total_max', params.total_max);
    const query = queryParams.toString();
    return fetchAPI(`/api/purchase-orders/admin${query ? `?${query}` : ''}`);
  },
  
  getPurchaseOrder: (poId) => fetchAPI(`/api/purchase-orders/admin/${poId}`),
  
  createPurchaseOrder: (data) => fetchAPI('/api/purchase-orders/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updatePurchaseOrder: (poId, data) => fetchAPI(`/api/purchase-orders/admin/${poId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  sendPurchaseOrder: (poId) => fetchAPI(`/api/purchase-orders/admin/${poId}/send`, {
    method: 'POST',
  }),
  
  cancelPurchaseOrder: (poId) => fetchAPI(`/api/purchase-orders/admin/${poId}/cancel`, {
    method: 'POST',
  }),
  
  closePurchaseOrder: (poId) => fetchAPI(`/api/purchase-orders/admin/${poId}/close`, {
    method: 'POST',
  }),
  
  // Bulk actions
  bulkDelete: (data) => fetchAPI('/api/purchase-orders/admin/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  restore: (poId) => fetchAPI(`/api/purchase-orders/admin/${poId}/restore`, {
    method: 'POST',
  }),
  
  // PO Lines
  addLine: (poId, data) => fetchAPI(`/api/purchase-orders/admin/${poId}/lines`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateLine: (poId, lineId, data) => fetchAPI(`/api/purchase-orders/admin/${poId}/lines/${lineId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  deleteLine: (poId, lineId) => fetchAPI(`/api/purchase-orders/admin/${poId}/lines/${lineId}`, {
    method: 'DELETE',
  }),
  
  // PDF Export - Single PO
  downloadPDF: async (poId) => {
    const res = await fetch(`${API_BASE_URL}/api/purchase-orders/admin/${poId}/pdf`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to download PDF');
    const blob = await res.blob();
    const filename = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'PO.pdf';
    return { blob, filename };
  },
  
  // PDF Export - Multiple POs (merged or ZIP)
  exportPDFs: async (poIds, format = 'merged') => {
    const res = await fetch(`${API_BASE_URL}/api/purchase-orders/admin/export-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_ids: poIds, format }),
    });
    if (!res.ok) throw new Error('Failed to export PDFs');
    const blob = await res.blob();
    const filename = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 
      (format === 'zip' ? 'PurchaseOrders.zip' : 'PurchaseOrders.pdf');
    return { blob, filename };
  },
};

// =====================================================
// VENDOR BILLS API (P0 Expenses Module)
// =====================================================

export const vendorBillsAPI = {
  // Enhanced list with server-side sorting, filtering, pagination
  getBills: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.status) queryParams.append('status', params.status);
    if (params.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    if (params.due_from) queryParams.append('due_from', params.due_from);
    if (params.due_to) queryParams.append('due_to', params.due_to);
    if (params.amount_min) queryParams.append('amount_min', params.amount_min);
    if (params.amount_max) queryParams.append('amount_max', params.amount_max);
    const query = queryParams.toString();
    return fetchAPI(`/api/vendor-bills/admin${query ? `?${query}` : ''}`);
  },
  
  getBill: (billId) => fetchAPI(`/api/vendor-bills/admin/${billId}`),
  
  getOpenBills: (vendorId) => fetchAPI(`/api/vendor-bills/admin/open?vendor_id=${vendorId}`),
  
  batchFetch: (ids) => fetchAPI('/api/vendor-bills/admin/batch', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  }),
  
  createBill: (data) => fetchAPI('/api/vendor-bills/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateBill: (billId, data) => fetchAPI(`/api/vendor-bills/admin/${billId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  voidBill: (billId, reason) => fetchAPI(`/api/vendor-bills/admin/${billId}/void?reason=${encodeURIComponent(reason || '')}`, {
    method: 'POST',
  }),
  
  // Finalize bill (draft → unpaid)
  finalizeBill: (billId) => fetchAPI(`/api/vendor-bills/admin/${billId}/finalize`, {
    method: 'POST',
  }),
  
  // Reopen bill to draft (Phase 5)
  reopenToDraft: (billId, reason) => fetchAPI(`/api/vendor-bills/admin/${billId}/reopen?reason=${encodeURIComponent(reason || '')}`, {
    method: 'POST',
  }),
  
  // Sync bill from PO
  syncFromPO: (billId) => fetchAPI(`/api/vendor-bills/admin/${billId}/sync-from-po`, {
    method: 'POST',
  }),
  
  // Check PO mismatch
  checkPOMismatch: (billId) => fetchAPI(`/api/vendor-bills/admin/${billId}/po-mismatch`),
  
  // Bulk actions
  bulkDelete: (data) => fetchAPI('/api/vendor-bills/admin/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  restore: (billId) => fetchAPI(`/api/vendor-bills/admin/${billId}/restore`, {
    method: 'POST',
  }),
  
  // ===================
  // FINAL AMOUNT (Simple SO → Final Invoice Flow)
  // ===================
  
  // Mark bill with vendor's final invoice amount
  // is_final: true when final invoice received
  // final_amount_cents: the actual invoice amount (required when is_final=true)
  updateFinalAmount: (billId, { is_final, final_amount_cents }) => fetchAPI(`/api/vendor-bills/admin/${billId}/final-amount`, {
    method: 'PATCH',
    body: JSON.stringify({ is_final, final_amount_cents }),
  }),
};

// =====================================================
// PAYMENTS API (P0 Expenses Module)
// =====================================================

export const paymentsAPI = {
  getPayments: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.payment_type) queryParams.append('payment_type', params.payment_type);
    if (params.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    if (params.customer_id) queryParams.append('customer_id', params.customer_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/payments/admin${query ? `?${query}` : ''}`);
  },
  
  getPayment: (paymentId) => fetchAPI(`/api/payments/admin/${paymentId}`),
  
  createPayment: (data) => fetchAPI('/api/payments/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  applyPayment: (paymentId, applications) => fetchAPI(`/api/payments/admin/${paymentId}/apply`, {
    method: 'POST',
    body: JSON.stringify({ applications }),
  }),
  
  voidPayment: (paymentId) => fetchAPI(`/api/payments/admin/${paymentId}/void`, {
    method: 'POST',
  }),
  
  // Get customer's open invoices for payment recording
  getCustomerOpenInvoices: (customerId) => fetchAPI(`/api/payments/admin/customer/${customerId}/open-invoices`),
  
  // Get payments for a specific invoice/order
  getPaymentsForInvoice: (orderId) => fetchAPI(`/api/payments/admin?invoice_id=${orderId}`),
  
  // Send payment receipt via email
  sendReceipt: (paymentId, data = {}) => fetchAPI(`/api/payments/admin/${paymentId}/send-receipt`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// =====================================================
// TAX ZONES API (P1 Tax Management)
// =====================================================

export const taxAPI = {
  // Sales Tax CRUD
  listZones: (includeExpired = false) => fetchAPI(`/api/tax/zones${includeExpired ? '?include_expired=true' : ''}`),
  getZones: (includeExpired = false) => fetchAPI(`/api/tax/zones${includeExpired ? '?include_expired=true' : ''}`),
  
  getZone: (zoneId) => fetchAPI(`/api/tax/zones/${zoneId}`),
  
  createZone: (data) => fetchAPI('/api/tax/zones', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateZone: (zoneId, data) => fetchAPI(`/api/tax/zones/${zoneId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  deleteZone: (zoneId) => fetchAPI(`/api/tax/zones/${zoneId}`, {
    method: 'DELETE',
  }),
  
  setDefaultZone: (zoneId) => fetchAPI(`/api/tax/zones/${zoneId}/set-default`, {
    method: 'POST',
  }),
  
  expireZone: (zoneId) => fetchAPI(`/api/tax/zones/${zoneId}/expire`, {
    method: 'POST',
  }),
  
  // Tax Settings (Business State)
  getSettings: () => fetchAPI('/api/tax/settings'),
  
  updateSettings: (data) => fetchAPI('/api/tax/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Tax Calculation
  calculateTax: (data) => fetchAPI('/api/tax/calculate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// =====================================================
// REMINDER PROFILES API (P1 Read-Only)
// =====================================================

export const reminderProfilesAPI = {
  // List all profiles
  getProfiles: () => fetchAPI('/api/reminders/profiles'),
  
  // Get single profile
  getProfile: (profileId) => fetchAPI(`/api/reminders/profiles/${profileId}`),
  
  // Get recommended profile for terms
  getProfileForTerms: (paymentTermsSource, netTermsDays = 0) => 
    fetchAPI(`/api/reminders/profile-for-terms?payment_terms_source=${paymentTermsSource}&net_terms_days=${netTermsDays}`),
  
  // Create profile (admin only)
  createProfile: (data) => fetchAPI('/api/reminders/profiles', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update profile (admin only)
  updateProfile: (profileId, data) => fetchAPI(`/api/reminders/profiles/${profileId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Delete profile (admin only)
  deleteProfile: (profileId) => fetchAPI(`/api/reminders/profiles/${profileId}`, {
    method: 'DELETE',
  }),
};

// =====================================================
// REMINDER SETTINGS API (Automated Reminders)
// =====================================================

export const reminderSettingsAPI = {
  // Get settings
  getSettings: () => fetchAPI('/api/reminders/settings'),
  
  // Update settings
  updateSettings: (data) => fetchAPI('/api/reminders/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  // Process due reminders (manual run)
  processDue: () => fetchAPI('/api/reminders/process-due', {
    method: 'POST',
  }),
  
  // Get reminder history
  getHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.invoice_id) queryParams.append('invoice_id', params.invoice_id);
    if (params.order_id) queryParams.append('order_id', params.order_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.days) queryParams.append('days', params.days);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/reminders/history${query ? `?${query}` : ''}`);
  },
  
  // Get stats
  getStats: (days = 30) => fetchAPI(`/api/reminders/history/stats?days=${days}`),
};

// =====================================================
// HEALTH CHECK
// =====================================================

export const healthAPI = {
  check: () => fetchAPI('/api/health'),
};

// Shipping API (Unishipper integration)
export const shippingAPI = {
  getRates: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/shipping/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch shipping rates');
    }
    return response.json();
  },
  
  getOrigin: async () => {
    const response = await fetch(`${API_BASE_URL}/api/shipping/origin`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch shipping origin');
    return response.json();
  },
};

// =====================================================
// MANUFACTURERS API
// =====================================================

export const manufacturersAPI = {
  // Enhanced list with server-side sorting, filtering, pagination
  getManufacturers: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Standard pagination params
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    // Sorting
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    // Global search
    if (params.q) queryParams.append('q', params.q);
    // Filters
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    // Legacy params
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/manufacturers${query ? `?${query}` : ''}`);
  },
  
  getManufacturer: (id) => fetchAPI(`/api/manufacturers/${id}`),
  
  createManufacturer: (data) => fetchAPI('/api/manufacturers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateManufacturer: (id, data) => fetchAPI(`/api/manufacturers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteManufacturer: (id) => fetchAPI(`/api/manufacturers/${id}`, {
    method: 'DELETE',
  }),
  
  // Bulk actions
  bulkDelete: (data) => fetchAPI('/api/manufacturers/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  restore: (id) => fetchAPI(`/api/manufacturers/${id}/restore`, {
    method: 'POST',
  }),
  
  // Get products by manufacturer
  getProducts: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    const query = queryParams.toString();
    return fetchAPI(`/api/manufacturers/${id}/products${query ? `?${query}` : ''}`);
  },
};

// =====================================================
// ACTIVITY LOGS API
// =====================================================

export const activityLogsAPI = {
  /**
   * Get activity logs for a specific entity
   * @param {string} entityType - Entity type (ORDER, PURCHASE_ORDER, VENDOR, BILL)
   * @param {string} entityId - Entity ID
   * @param {number} limit - Max number of logs to return (default 10)
   */
  getEntityLogs: (entityType, entityId, limit = 10) => 
    fetchAPI(`/api/activity-logs/entity/${entityType}/${entityId}?limit=${limit}`),
  
  /**
   * List all activity logs with filters (admin view)
   */
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params.action) queryParams.append('action', params.action);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/activity-logs${query ? `?${query}` : ''}`);
  }
};

// =====================================================
// EMAIL TEMPLATES API
// =====================================================

export const emailTemplatesAPI = {
  /**
   * List all email templates
   */
  list: () => fetchAPI('/api/email-templates'),
  
  /**
   * Get template types with metadata
   */
  getTypes: () => fetchAPI('/api/email-templates/types'),
  
  /**
   * Get a single template by type
   */
  get: (templateType) => fetchAPI(`/api/email-templates/${templateType}`),
  
  /**
   * Update a template
   */
  update: (templateType, data) => fetchAPI(`/api/email-templates/${templateType}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  /**
   * Reset template to default
   */
  reset: (templateType) => fetchAPI(`/api/email-templates/${templateType}/reset`, {
    method: 'POST',
  }),
  
  /**
   * Preview template with sample data
   */
  preview: (templateType, data = {}) => fetchAPI(`/api/email-templates/${templateType}/preview`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  /**
   * Get sample data for a template type
   */
  getSampleData: (templateType) => fetchAPI(`/api/email-templates/${templateType}/sample-data`),
  
  /**
   * Send email using a template
   */
  send: (templateType, data) => fetchAPI(`/api/email-templates/${templateType}/send`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  /**
   * Send a test email
   */
  sendTest: (data) => fetchAPI('/api/email-templates/send-test', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  /**
   * Get email send history
   */
  getHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params.template_type) queryParams.append('template_type', params.template_type);
    if (params.days) queryParams.append('days', params.days);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/email-templates/logs/history${query ? `?${query}` : ''}`);
  },
  
  /**
   * Get email send statistics
   */
  getStats: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.days) queryParams.append('days', params.days);
    const query = queryParams.toString();
    return fetchAPI(`/api/email-templates/logs/stats${query ? `?${query}` : ''}`);
  },
};

// =====================================================
// BANK ACCOUNTS API (Check Printing)
// =====================================================

export const bankAccountsAPI = {
  // List all bank accounts
  getAccounts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const query = queryParams.toString();
    return fetchAPI(`/api/bank-accounts${query ? `?${query}` : ''}`);
  },
  
  // Get single bank account
  getAccount: (accountId) => fetchAPI(`/api/bank-accounts/${accountId}`),
  
  // Create bank account
  createAccount: (data) => fetchAPI('/api/bank-accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update bank account
  updateAccount: (accountId, data) => fetchAPI(`/api/bank-accounts/${accountId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Set as default
  setDefault: (accountId) => fetchAPI(`/api/bank-accounts/${accountId}/set-default`, {
    method: 'POST',
  }),
  
  // Delete bank account (soft delete if has checks)
  deleteAccount: (accountId) => fetchAPI(`/api/bank-accounts/${accountId}`, {
    method: 'DELETE',
  }),
  
  // Get next check number
  getNextCheckNumber: (accountId) => fetchAPI(`/api/bank-accounts/${accountId}/next-check-number`),
};

// =====================================================
// CHECKS API (Check Printing)
// =====================================================

export const checksAPI = {
  // List checks with filtering
  getChecks: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.bank_account_id) queryParams.append('bank_account_id', params.bank_account_id);
    if (params.vendor_id) queryParams.append('vendor_id', params.vendor_id);
    if (params.payment_id) queryParams.append('payment_id', params.payment_id);
    if (params.bill_id) queryParams.append('bill_id', params.bill_id);
    if (params.po_id) queryParams.append('po_id', params.po_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.payee) queryParams.append('payee', params.payee);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    const query = queryParams.toString();
    return fetchAPI(`/api/checks${query ? `?${query}` : ''}`);
  },
  
  // Get single check
  getCheck: (checkId) => fetchAPI(`/api/checks/${checkId}`),
  
  // Create check
  createCheck: (data) => fetchAPI('/api/checks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Update draft check
  updateCheck: (checkId, data) => fetchAPI(`/api/checks/${checkId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  
  // Mark check as printed
  printCheck: (checkId) => fetchAPI(`/api/checks/${checkId}/print`, {
    method: 'POST',
  }),
  
  // Void check
  voidCheck: (checkId, reason) => fetchAPI(`/api/checks/${checkId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  
  // Preview check from payment (without creating)
  previewFromPayment: (paymentId) => fetchAPI(`/api/checks/payment/${paymentId}/preview`),
  
  // Get check PDF URL (for download/print)
  getCheckPdfUrl: (checkId) => `${API_BASE_URL}/api/checks/${checkId}/pdf`,
};

// =====================================================
// V2 PRODUCTS API (New Template-Based Architecture)
// =====================================================

export const productsV2API = {
  // Get all products (with filtering)
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.template_id) queryParams.append('template_id', params.template_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/v2/products${query ? `?${query}` : ''}`);
  },
  
  // Get single product by ID
  getProduct: (productId) => fetchAPI(`/api/v2/products/${productId}`),
  
  // Get products by type (LGF, SMF, AWD, PROMO)
  getByType: (productType, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', productType);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/v2/products?${query}`);
  },
  
  // Get templates
  getTemplates: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.product_type) queryParams.append('product_type', params.product_type);
    if (params.status) queryParams.append('status', params.status);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/templates${query ? `?${query}` : ''}`);
  },
  
  // Get option sets
  getOptionSets: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.append('type', params.type);
    
    const query = queryParams.toString();
    return fetchAPI(`/api/option-sets${query ? `?${query}` : ''}`);
  },
  
  // Get option set by ID
  getOptionSet: (optionSetId) => fetchAPI(`/api/option-sets/${optionSetId}`),
};

// =====================================================
// V2 PRICING API (Unified Pricing Calculator)
// =====================================================

export const pricingV2API = {
  // Calculate price for a product configuration
  calculate: (data) => fetchAPI('/api/pricing/v2/calculate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Preview pricing for a template (admin use)
  preview: (data) => fetchAPI('/api/pricing/v2/preview', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  // Calculate markup
  calculateMarkup: (data) => fetchAPI('/api/pricing/v2/markup/calculate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

export default {
  smallFormat: smallFormatAPI,
  largeFormat: largeFormatAPI,
  swag: swagAPI,
  awards: awardsAPI,
  search: searchAPI,
  contact: contactAPI,
  auth: authAPI,
  orders: ordersAPI,
  admin: adminAPI,
  catalog: catalogAPI,
  unified: unifiedAPI,
  productTypes: productTypesAPI,
  health: healthAPI,
  shipping: shippingAPI,
  crm: crmAPI,
  quotes: quotesAPI,  // DEPRECATED - use orderLifecycle
  operations: operationsAPI,
  invoices: invoicesAPI,  // DEPRECATED - use orderLifecycle
  orderLifecycle: orderLifecycleAPI,  // New unified quote/invoice API
  vendors: vendorsAPI,
  purchaseOrders: purchaseOrdersAPI,
  vendorBills: vendorBillsAPI,
  payments: paymentsAPI,
  manufacturers: manufacturersAPI,
  tax: taxAPI,  // P1 Tax Zones
  reminderProfiles: reminderProfilesAPI,  // P1 Reminder Profiles
  activityLogs: activityLogsAPI,  // Activity logging system
  bankAccounts: bankAccountsAPI,  // Check Printing - Bank Accounts
  checks: checksAPI,  // Check Printing - Checks
  productsV2: productsV2API,  // V2 Template-Based Products
  pricingV2: pricingV2API,  // V2 Pricing Calculator
};
